import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2, Plus, Pencil, Trash2, UserCheck, GraduationCap, Crown } from "lucide-react";

const ROLE_META = {
  wali_kelas: { label: "Wali Kelas", icon: UserCheck, color: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  guru_mapel: { label: "Guru Mata Pelajaran", icon: GraduationCap, color: "bg-blue-100 text-blue-700 border-blue-200" },
  kepala_madrasah: { label: "Kepala Madrasah", icon: Crown, color: "bg-amber-100 text-amber-700 border-amber-200" },
};

function emptyForm() {
  return {
    ustadz_id: "", role_type: "guru_mapel",
    tahun_ajaran: "", semester: "ganjil",
    kelas_id: "", mata_pelajaran_id: "",
    is_active: true, keterangan: "",
  };
}

export default function AssignmentsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ustadzList, setUstadzList] = useState([]);
  const [kelasList, setKelasList] = useState([]);
  const [mapelList, setMapelList] = useState([]);
  const [taList, setTaList] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [filterRole, setFilterRole] = useState("");
  const [filterTA, setFilterTA] = useState("");

  const loadRefs = async () => {
    const [u, k, m, ta] = await Promise.all([
      api.get("/ustadz?limit=500"),
      api.get("/kelas?limit=500"),
      api.get("/mata-pelajaran"),
      api.get("/tahun-ajaran"),
    ]);
    setUstadzList(u.data.items || []);
    setKelasList(k.data.items || []);
    setMapelList(m.data.items || []);
    setTaList(ta.data.items || []);
  };

  const loadItems = async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams();
      if (filterRole) q.append("role_type", filterRole);
      if (filterTA) q.append("tahun_ajaran", filterTA);
      const r = await api.get("/assignments?" + q.toString());
      setItems(r.data.items || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadRefs(); }, []);
  useEffect(() => { loadItems(); }, [filterRole, filterTA]);

  const openCreate = () => {
    const activeTA = taList.find(t => t.is_active);
    setEditing(null);
    setForm({ ...emptyForm(), tahun_ajaran: activeTA?.tahun_ajaran || "", semester: activeTA?.semester || "ganjil" });
    setDialogOpen(true);
  };
  const openEdit = (it) => {
    setEditing(it);
    setForm({
      ustadz_id: it.ustadz_id, role_type: it.role_type,
      tahun_ajaran: it.tahun_ajaran || "", semester: it.semester || "ganjil",
      kelas_id: it.kelas_id || "", mata_pelajaran_id: it.mata_pelajaran_id || "",
      is_active: it.is_active, keterangan: it.keterangan || "",
    });
    setDialogOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      if (payload.role_type === "kepala_madrasah") {
        payload.kelas_id = null;
        payload.mata_pelajaran_id = null;
      } else if (payload.role_type === "wali_kelas") {
        payload.mata_pelajaran_id = null;
      }
      if (editing) {
        // update only allowed fields
        const upd = { tahun_ajaran: payload.tahun_ajaran, semester: payload.semester, kelas_id: payload.kelas_id, mata_pelajaran_id: payload.mata_pelajaran_id, is_active: payload.is_active, keterangan: payload.keterangan };
        await api.patch(`/assignments/${editing.id}`, upd);
      } else {
        await api.post("/assignments", payload);
      }
      toast.success("Assignment tersimpan");
      setDialogOpen(false);
      loadItems();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (it) => {
    if (!confirm(`Hapus assignment ${ROLE_META[it.role_type]?.label} untuk ${it.ustadz?.nama || it.ustadz_id}?`)) return;
    try { await api.delete(`/assignments/${it.id}`); toast.success("Terhapus"); loadItems(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const grouped = useMemo(() => {
    const g = { wali_kelas: [], guru_mapel: [], kepala_madrasah: [] };
    items.forEach(it => { (g[it.role_type] = g[it.role_type] || []).push(it); });
    return g;
  }, [items]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Penugasan Ustadz</h1>
          <p className="text-sm text-slate-500">Kelola role & assignment: wali kelas, guru mata pelajaran, kepala madrasah.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Select value={filterRole || "all"} onValueChange={(v) => setFilterRole(v === "all" ? "" : v)}>
            <SelectTrigger className="w-[190px]"><SelectValue placeholder="Semua Role" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua Role</SelectItem>
              <SelectItem value="wali_kelas">Wali Kelas</SelectItem>
              <SelectItem value="guru_mapel">Guru Mapel</SelectItem>
              <SelectItem value="kepala_madrasah">Kepala Madrasah</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterTA || "all"} onValueChange={(v) => setFilterTA(v === "all" ? "" : v)}>
            <SelectTrigger className="w-[190px]"><SelectValue placeholder="Semua Tahun Ajaran" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua Tahun Ajaran</SelectItem>
              {taList.map(t => <SelectItem key={t.id} value={t.tahun_ajaran}>{t.tahun_ajaran} — {t.semester}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={openCreate} className="bg-emerald-600 hover:bg-emerald-700">
            <Plus className="h-4 w-4 mr-1" /> Tambah
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-emerald-600" /></div>
      ) : items.length === 0 ? (
        <Card><CardContent className="py-10 text-center text-slate-500">Belum ada penugasan. Klik "Tambah" untuk memulai.</CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {Object.entries(grouped).map(([roleType, list]) => {
            const meta = ROLE_META[roleType];
            const Icon = meta.icon;
            return (
              <Card key={roleType}>
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Icon className="h-4 w-4 text-emerald-600" /> {meta.label} <Badge variant="outline" className="ml-auto">{list.length}</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {list.length === 0 && <p className="text-xs text-slate-400">Belum ada.</p>}
                  {list.map(it => (
                    <div key={it.id} className="p-3 rounded-lg border border-slate-200 bg-white hover:border-emerald-300 transition">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="font-medium text-slate-800 truncate">{it.ustadz?.nama || "?"}</p>
                          <p className="text-xs text-slate-500 truncate">
                            {it.kelas?.nama ? `Kelas: ${it.kelas.nama}` : (roleType === "kepala_madrasah" ? "—" : <span className="text-amber-600">Kelas belum diset</span>)}
                            {it.mata_pelajaran?.nama && <> · {it.mata_pelajaran.nama}</>}
                          </p>
                          <p className="text-xs text-slate-400">{it.tahun_ajaran} · {it.semester}</p>
                        </div>
                        <div className="flex gap-1 shrink-0">
                          <Button size="icon" variant="ghost" onClick={() => openEdit(it)}><Pencil className="h-3.5 w-3.5" /></Button>
                          <Button size="icon" variant="ghost" onClick={() => remove(it)}><Trash2 className="h-3.5 w-3.5 text-red-500" /></Button>
                        </div>
                      </div>
                      {!it.is_active && <Badge variant="outline" className="mt-1 text-xs bg-slate-100">Nonaktif</Badge>}
                    </div>
                  ))}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Penugasan" : "Tambah Penugasan"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div>
              <Label>Ustadz</Label>
              <Select value={form.ustadz_id} onValueChange={(v) => setForm(f => ({ ...f, ustadz_id: v }))} disabled={!!editing}>
                <SelectTrigger><SelectValue placeholder="Pilih ustadz" /></SelectTrigger>
                <SelectContent>
                  {ustadzList.map(u => <SelectItem key={u.id} value={u.id}>{u.nama}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Jenis Penugasan</Label>
              <Select value={form.role_type} onValueChange={(v) => setForm(f => ({ ...f, role_type: v }))} disabled={!!editing}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="wali_kelas">Wali Kelas</SelectItem>
                  <SelectItem value="guru_mapel">Guru Mata Pelajaran</SelectItem>
                  <SelectItem value="kepala_madrasah">Kepala Madrasah</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Tahun Ajaran</Label>
                <Select value={form.tahun_ajaran || "none"} onValueChange={(v) => setForm(f => ({ ...f, tahun_ajaran: v === "none" ? "" : v }))}>
                  <SelectTrigger><SelectValue placeholder="Pilih" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">—</SelectItem>
                    {taList.map(t => <SelectItem key={t.id} value={t.tahun_ajaran}>{t.tahun_ajaran}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Semester</Label>
                <Select value={form.semester} onValueChange={(v) => setForm(f => ({ ...f, semester: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ganjil">Ganjil</SelectItem>
                    <SelectItem value="genap">Genap</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {form.role_type !== "kepala_madrasah" && (
              <div>
                <Label>Kelas</Label>
                <Select value={form.kelas_id || "none"} onValueChange={(v) => setForm(f => ({ ...f, kelas_id: v === "none" ? "" : v }))}>
                  <SelectTrigger><SelectValue placeholder="Pilih kelas" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">—</SelectItem>
                    {kelasList.map(k => <SelectItem key={k.id} value={k.id}>{k.nama} ({k.tingkatan})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            {form.role_type === "guru_mapel" && (
              <div>
                <Label>Mata Pelajaran</Label>
                <Select value={form.mata_pelajaran_id || "none"} onValueChange={(v) => setForm(f => ({ ...f, mata_pelajaran_id: v === "none" ? "" : v }))}>
                  <SelectTrigger><SelectValue placeholder="Pilih mata pelajaran" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">—</SelectItem>
                    {mapelList.map(m => <SelectItem key={m.id} value={m.id}>{m.nama}{m.tingkatan ? ` (${m.tingkatan})` : ""}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label>Keterangan (opsional)</Label>
              <Input value={form.keterangan} onChange={(e) => setForm(f => ({ ...f, keterangan: e.target.value }))} />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.is_active} onChange={(e) => setForm(f => ({ ...f, is_active: e.target.checked }))} className="h-4 w-4" />
              Aktif
            </label>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Batal</Button>
              <Button type="submit" disabled={saving} className="bg-emerald-600 hover:bg-emerald-700">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Simpan"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
